import { FC, ReactNode } from 'react'

import { Text, Card, StepsProgressBar, Flex } from '@sequencehq/core-components'
import {
  GreyGrey10,
  GreyGrey50,
  GreyGrey60,
  GreyGrey80,
  Primary12Regular,
  Primary13SemiBold
} from '@sequencehq/core-components'

type Props = {
  title: string
  stepsCompleted: number
  isActive: boolean
  caption?: ReactNode
  /** Sits at the far end of the caption row, e.g. "0 of 2 signed". */
  secondaryCaption?: string
  children: ReactNode
  'data-testid'?: string
}

const SignatureProgressCardComponent: FC<Props> = props => (
  <Card
    display="flex"
    flexDirection="column"
    padding="8px"
    gap="8px"
    data-testid={props['data-testid']}
  >
    <Flex
      flexDir="column"
      backgroundColor={GreyGrey10}
      borderRadius="4px"
      padding="8px"
      gap="8px"
    >
      <Text
        {...Primary13SemiBold}
        color={GreyGrey80}
        data-testid={
          props['data-testid'] ? `${props['data-testid']}.title` : undefined
        }
      >
        {props.title}
      </Text>
      <StepsProgressBar
        stepsTotal={3}
        stepsCompleted={props.stepsCompleted}
        isActive={props.isActive}
        data-testid={
          props['data-testid']
            ? `${props['data-testid']}.progressBar`
            : undefined
        }
        stepTooltips={[
          'Quote published',
          'Awaiting signatures',
          'Quote executed'
        ]}
      />
      {(props.caption || props.secondaryCaption) && (
        <Flex alignItems="center" gap="8px">
          {typeof props.caption === 'string' ? (
            <Caption
              data-testid={
                props['data-testid']
                  ? `${props['data-testid']}.caption`
                  : undefined
              }
            >
              {props.caption}
            </Caption>
          ) : (
            props.caption
          )}

          {props.secondaryCaption && (
            <Text
              {...Primary12Regular}
              color={GreyGrey50}
              marginLeft="auto"
              whiteSpace="nowrap"
              data-testid={
                props['data-testid']
                  ? `${props['data-testid']}.secondaryCaption`
                  : undefined
              }
            >
              {props.secondaryCaption}
            </Text>
          )}
        </Flex>
      )}
    </Flex>

    <Flex gap="8px">{props.children}</Flex>
  </Card>
)

const Caption: FC<{ children: ReactNode; 'data-testid'?: string }> = ({
  children,
  'data-testid': dataTestId
}) => {
  return (
    <Text {...Primary12Regular} color={GreyGrey60} data-testid={dataTestId}>
      {children}
    </Text>
  )
}

export const SignatureProgressCard = Object.assign(
  SignatureProgressCardComponent,
  { Caption }
)
